import React, { useState, useMemo } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    IconButton,
    TextField,
    InputAdornment,
    Stack,
    Pagination,
    TableSortLabel,
    PaginationItem,
    CircularProgress,
    Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

// Reusable table styles
export const tableStyles = {
    tableContainer: {
        boxShadow: 'none',
        border: 'none',
        width: '100%',
        background: '#F4F4F4',
        overflowX: 'auto',
        overflowY: 'visible'
    },
    headerCell: {
        fontFamily: 'Poppins',
        fontSize: '14px',
        fontWeight: 600,
        color: '#666',
        borderBottom: '1px solid #E0E0E0',
        padding: '12px 16px',
        whiteSpace: 'nowrap',
        background: '#fff'
    },
    tableCell: {
        fontFamily: 'Poppins',
        fontSize: '13px',
        color: '#333',
        borderBottom: '1px solid #E0E0E0',
        padding: '12px 16px',
        whiteSpace: 'nowrap',
        background: '#fff'
    },
    searchContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0px',
        padding: '16px',
        borderBottom: '1px solid #e0e0e0',
        position: 'relative',
        zIndex: 10,
        flexWrap: 'wrap',
        gap: '12px'
    },
    searchField: {
        '& .MuiOutlinedInput-root': {
            height: '36px',
            borderRadius: '4px',
            '& fieldset': {
                borderColor: '#E0E0E0'
            },
            '&:hover fieldset': {
                borderColor: '#9AA6AC'
            }
        },
        '& .MuiOutlinedInput-input': {
            fontSize: '14px',
            fontFamily: 'Poppins',
            padding: '8px 14px'
        }
    },
    pagination: {
        padding: '16px',
        background: '#fff',
        '& .MuiPaginationItem-root': {
            fontFamily: 'Poppins',
            fontSize: '13px',
            minWidth: 'auto',
            margin: '0 2px'
        },
        '& .MuiPaginationItem-page': {
            color: '#666',
            fontWeight: 500,
            '&.Mui-selected': {
                backgroundColor: '#0a0068',
                color: '#fff',
                '&:hover': {
                    backgroundColor: '#080052'
                }
            },
            '&:hover': {
                backgroundColor: '#e8e8e8'
            }
        },
        '& .MuiPaginationItem-previousNext': {
            minWidth: 'auto',
            backgroundColor: '#0a0068',
            color: '#fff',
            fontWeight: 500,
            '&:hover': {
                backgroundColor: '#080052'
            },
            '&.Mui-disabled': {
                opacity: 0.5,
                backgroundColor: '#e0e0e0',
                color: '#666'
            }
        }
    }
};

const CommonTable = ({
    columns,
    data,
    searchPlaceholder = 'Search...',
    actionButtons = null,
    sortable = true,
    searchable = true,
    pageSize = 10,
    onRowClick,
    renderCustomCell,
    isLoading = false,
    emptyMessage = 'No data available'
}) => {
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;
        
        return data.filter(row =>
            columns.some(column => {
                const cellValue = row[column.key];
                return cellValue && String(cellValue).toLowerCase().includes(searchTerm.toLowerCase());
            })
        );
    }, [data, columns, searchTerm]);

    // Handle sorting
    const handleSort = (columnKey) => {
        if (!sortable) return;
        
        let direction = 'asc';
        if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key: columnKey, direction });
    };

    // Sort and paginate data
    const sortedAndPaginatedData = useMemo(() => {
        let processed = [...filteredData];
        
        if (sortConfig.key) {
            processed.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
                
                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        const startIndex = (page - 1) * pageSize;
        return processed.slice(startIndex, startIndex + pageSize);
    }, [filteredData, sortConfig, page, pageSize]);

    return (
        <Box>
            {/* Search and Action Buttons Section */}
            {(searchable || actionButtons) && (
                <Box sx={tableStyles.searchContainer}>
                    {searchable && (
                        <TextField
                            placeholder={searchPlaceholder}
                            size="small"
                            sx={tableStyles.searchField}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#9AA6AC', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                    {actionButtons}
                </Box>
            )}

            {/* Table Section */}
            <TableContainer component={Paper} sx={tableStyles.tableContainer}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.key}
                                    sx={{
                                        ...tableStyles.headerCell,
                                        textAlign: column.align || 'left'
                                    }}
                                    style={{ minWidth: column.minWidth }}
                                >
                                    {sortable ? (
                                        <TableSortLabel
                                            active={sortConfig.key === column.key}
                                            direction={sortConfig.key === column.key ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort(column.key)}
                                            sx={{
                                                justifyContent: column.align === 'center' ? 'center' : 'flex-start',
                                                '& .MuiTableSortLabel-icon': {
                                                    marginLeft: column.align === 'center' ? '4px' : '0px'
                                                }
                                            }}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : (
                                        <div style={{ textAlign: column.align || 'left' }}>
                                            {column.label}
                                        </div>
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 4 }}>
                                    <CircularProgress size={40} sx={{ color: '#0a0068' }} />
                                    <Typography sx={{ mt: 2, fontFamily: 'Poppins', color: '#666', fontSize: '14px' }}>
                                        Loading data...
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : sortedAndPaginatedData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography sx={{ fontFamily: 'Poppins', color: '#999', fontSize: '14px' }}>
                                        {emptyMessage}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedAndPaginatedData.map((row, index) => (
                                <TableRow
                                    key={row.id || index}
                                    onClick={() => onRowClick && onRowClick(row)}
                                    sx={onRowClick ? { cursor: 'pointer' } : undefined}
                                >
                                    {columns.map((column) => (
                                        <TableCell key={column.key} sx={tableStyles.tableCell}>
                                            {renderCustomCell ? 
                                                renderCustomCell(column.key, row[column.key], row, ((page - 1) * pageSize) + index) :
                                                row[column.key]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination Section */}
            <Box sx={{
                ...tableStyles.pagination,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box sx={{ fontFamily: 'Poppins', fontSize: '13px', color: '#666' }}>
                    Showing {Math.min((page - 1) * pageSize + 1, filteredData.length)} to{' '}
                    {Math.min(page * pageSize, filteredData.length)} of {filteredData.length}
                </Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Pagination
                        count={Math.ceil(filteredData.length / pageSize)}
                        page={page}
                        onChange={(e, newPage) => setPage(newPage)}
                        shape="rounded"
                        showFirstButton={false}
                        showLastButton={false}
                        size="small"
                        hidePrevButton={page === 1}
                        hideNextButton={page === Math.ceil(filteredData.length / pageSize)}
                        renderItem={(item) => (
                            <PaginationItem
                                slots={{
                                    previous: (props) => (
                                        <Box sx={{
                                            fontSize: '13px',
                                            color: '#fff',
                                            px: 2,
                                            py: 0.5,
                                            bgcolor: '#0a0068',
                                            borderRadius: 1,
                                            '&:hover': {
                                                bgcolor: '#080052'
                                            }
                                        }}>
                                            Previous
                                        </Box>
                                    ),
                                    next: (props) => (
                                        <Box sx={{
                                            fontSize: '13px',
                                            color: '#fff',
                                            px: 2,
                                            py: 0.5,
                                            bgcolor: '#0a0068',
                                            borderRadius: 1,
                                            '&:hover': {
                                                bgcolor: '#080052'
                                            }
                                        }}>
                                            Next
                                        </Box>
                                    )
                                }}
                                {...item}
                                sx={{
                                    '&.MuiPaginationItem-root': {
                                        margin: '0 4px'
                                    }
                                }}
                            />
                        )}
                    />
                </Stack>
            </Box>
        </Box>
    );
};

export default CommonTable;